'use client'

import {observer} from 'mobx-react'
import React, {useEffect, useState} from 'react'

import {usePageProps} from '@/src/shared/modules/page/page-content-provider'
import {Body} from '@/src/shared/ui/layout'
import {LayoutUI} from '@/src/shared/ui/layout/ui/styles'
import {cn, isMobile, isWeb, replaceMobileImageUrls} from '@/src/shared/utils/functions'

import {PageTitle} from './page-title'
import {getInjection} from '@runlab/di'
import {LayoutEventTypes} from '@/src/core/domain/events/layout.events'
import styled from 'styled-components'
import {LoadingIcon} from '@/src/shared/ui/icons/loading'
import {usePathname, useSearchParams} from 'next/navigation'
import Script from 'next/script'
import {settings} from '@/src/config/settings'
import {disableAnalytic} from '@/src/config/analythic'

export const PageContent = observer(({children}: React.PropsWithChildren) => {
  const pagePropsStore = usePageProps()
  const customClassName = pagePropsStore.options?.className
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getInjection('IEventBus').subscribe(LayoutEventTypes.StartRefreshPageData, () => {
      setLoading(true)
    })
    getInjection('IEventBus').subscribe(LayoutEventTypes.EndRefreshPageData, () => {
      setLoading(false)
    })
  }, [])

  if (loading) {
    return <LoadingStub />
  }

  return (
    <LayoutUI.Main
      {...(pagePropsStore.options?.theme == 'dark' && {'data-dark': true})}
      className="main">
      <LayoutUI.Container
        className={cn('container', {
          'no-width-limit': pagePropsStore.options?.limit === false,
          'no-padding': pagePropsStore.options?.padding === false,
          center: pagePropsStore.options?.center === true,
          ...(customClassName && {[customClassName]: true}),
        })}>
        <PageTitle />

        <Body>
          {children}
          <ImageReplace />

          {!disableAnalytic &&
            settings.DIGI_SEARCH_ID &&
            (!['test', 'local', 'development'].includes(settings.NEXT_PUBLIC_APP_ENV) ||
              isMobile()) && <DigineticaScripts />}
        </Body>
      </LayoutUI.Container>
    </LayoutUI.Main>
  )
})

const LoadingStub = () => {
  return (
    <LoadingContainer>
      <LoadingIcon />
    </LoadingContainer>
  )
}

const ImageReplace = () => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  useEffect(() => {
    if (!isWeb()) {
      replaceMobileImageUrls()
    }
  }, [pathname, searchParams])

  return null
}

const DigineticaScripts = () => (
  <>
    <link rel="preconnect" href="https://cdn.diginetica.net/" />
    <link rel="preconnect" href="https://tracking.diginetica.net/" />
    <link rel="preconnect" href="https://tracking-app.diginetica.net/" />

    <Script
      id="digi-script"
      dangerouslySetInnerHTML={{
        __html: `
              const digiScript = document.createElement('script');
              digiScript.src = 'https://cdn.diginetica.net/${settings.DIGI_SEARCH_ID}/client.js';
              digiScript.defer = true;
              digiScript.async = true;
              document.body.prepend(digiScript);
      `,
      }}
    />
  </>
)

const LoadingContainer = styled.div``
